import { observer } from 'mobx-react';
import { FC, useMemo } from 'react';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { Card } from '@monorepo/base/src/components/card/card';
import { SelectionList } from '@monorepo/base/src/components/selection-list/selection-list';
import { CreativeModel } from '../../../../models/creative.model';
import { CampaignCreativeModel } from '../../../../models/campaign-creative.model';
import { IAdminxStore } from '../../../../stores';
import { SelectionListOption } from '../../../selection-list-option/selection-list-option';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import styles from './destination-creatives.module.scss';

interface IAttachCreativeOption {
	name: string;
	description: string;
}

function toCreativeOption(creative: CreativeModel): IAttachCreativeOption {
	return {
		name: `${creative.getBrand()} - ${creative.getId()}` || '',
		description: `${creative.getCreativeGroup()?.getAdvertiser()?.getName()} - ${creative.getCreativeGroup()?.getDesign()}`,
	};
}

function parseCreativeIdFromOption(option: IAttachCreativeOption): id | undefined {
	const match = option.name.match(/- (\d+)$/);
	return match ? match[1] : undefined;
}

interface DestinationCreativesProps {
	isLoadingCreatives?: boolean;
}

export const DestinationCreatives: FC<DestinationCreativesProps> = observer(({ isLoadingCreatives = false }) => {
	const { destinationStore, creativeStore } = useStores<IAdminxStore>();
	const creativeList = creativeStore.getListStore().getList();
	const destination = destinationStore.getCrud().getData();
	const advertiserId = destination.getAdvertiser()?.getId();
	const linkedCreatives = destination.getCreatives();

	const selectedCreatives = useMemo<IAttachCreativeOption[]>(
		() => linkedCreatives.filter(link => link.getCreative()?.getId() != null).map(link => toCreativeOption(link.getCreative()!)),
		[linkedCreatives]
	);

	const creativeOptions = useMemo(
		() =>
			creativeList
				.filter(creative => creative.getCreativeGroup()?.getAdvertiser()?.getId() === advertiserId)
				.map(creative => toCreativeOption(creative)),
		[creativeList, advertiserId]
	);

	const onCreativeSelect = (options: IAttachCreativeOption[]) => {
		const existingByCreativeId = new Map(
			linkedCreatives.filter(link => link.getCreative()?.getId() != null).map(link => [String(link.getCreative()!.getId()), link])
		);

		const nextCreatives = options
			.map(option => parseCreativeIdFromOption(option))
			.filter((creativeId): creativeId is id => creativeId != null)
			.map(creativeId => {
				const existing = existingByCreativeId.get(String(creativeId));
				if (existing) {
					return new CampaignCreativeModel(existing);
				}

				const creative = creativeList.find(item => String(item.getId()) === String(creativeId));
				return new CampaignCreativeModel({
					creative: creative ? new CreativeModel(creative) : new CreativeModel({ id: creativeId }),
				});
			});

		destination.setCreatives(nextCreatives);
	};

	return (
		<Card
			title="Creatives"
			icon="layers-three-02"
			className={styles.card}
			debugProps={{ dataAttrs: [new DataAttribute('id', 'destination_creatives_section')] }}>
			<SelectionList<IAttachCreativeOption>
				label="Creatives"
				options={creativeOptions}
				defaultOptions={selectedCreatives}
				onSelect={(creative: IAttachCreativeOption[]) => onCreativeSelect(creative)}
				isLoadingOptions={creativeStore.getListStore().getIsLoading() || isLoadingCreatives}
				isSelectionList={true}
				renderOption={option => <SelectionListOption option={option} />}
				debugProps={{ dataAttrs: [new DataAttribute('id', 'destination_creatives_select')] }}
			/>
		</Card>
	);
});
